/**
 * New Arrivals Section
 * Handles category tab filtering and slider functionality
 */

class NewArrivalsSection {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.categoryTabs = this.section.querySelectorAll('.new-arrivals__category-tab');
    this.productItems = this.section.querySelectorAll('.new-arrivals__product-item');
    this.slider = this.section.querySelector('.new-arrivals__slider');
    this.activeCategory = null;

    if (!this.section || this.categoryTabs.length === 0) return;

    this.init();
  }

  init() {
    // Set up category tab click handlers
    this.categoryTabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        const category = tab.dataset.category;
        this.filterByCategory(category);
        this.updateActiveTab(tab);
      });
    });

    // Initialize: show first category's products
    if (this.categoryTabs.length > 0) {
      const firstCategory = this.categoryTabs[0].dataset.category;
      this.filterByCategory(firstCategory);
    }

    // Initialize wishlist functionality
    this.initWishlist();
  }

  initWishlist() {
    const heartButtons = this.section.querySelectorAll('.card-product-premium__heart');
    
    heartButtons.forEach((button) => {
      const productId = button.getAttribute('data-product-id');
      const productHandle = button.getAttribute('data-product-handle');
      const productUrl = button.getAttribute('data-product-url');
      const productTitle = button.getAttribute('data-product-title');
      
      // Check if product is in wishlist
      const isInWishlist = () => {
        try {
          const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
          return wishlist.some((item) => {
            return item.id === productId || item.handle === productHandle;
          });
        } catch (e) {
          return false;
        }
      };
      
      // Update button state
      const updateButtonState = () => {
        if (isInWishlist()) {
          button.classList.add('card-product-premium__heart--active');
          button.setAttribute('aria-label', 'Remove from wishlist');
        } else {
          button.classList.remove('card-product-premium__heart--active');
          button.setAttribute('aria-label', 'Add to wishlist');
        }
      };
      
      // Add/remove from wishlist
      const toggleWishlist = () => {
        try {
          let wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
          const productData = {
            id: productId,
            handle: productHandle,
            url: productUrl,
            title: productTitle
          };
          
          const existingIndex = wishlist.findIndex((item) => {
            return item.id === productId || item.handle === productHandle;
          });
          
          if (existingIndex > -1) {
            // Remove from wishlist
            wishlist.splice(existingIndex, 1);
          } else {
            // Add to wishlist
            wishlist.push(productData);
          }
          
          localStorage.setItem('wishlist', JSON.stringify(wishlist));
          updateButtonState();
          
          // Dispatch event for other components
          window.dispatchEvent(new CustomEvent('wishlist:updated', {
            detail: { wishlist: wishlist }
          }));
          
          // Trigger storage event for cross-tab sync
          window.dispatchEvent(new StorageEvent('storage', {
            key: 'wishlist',
            newValue: JSON.stringify(wishlist)
          }));
        } catch (e) {
          console.error('Error updating wishlist:', e);
        }
      };
      
      // Initialize button state
      updateButtonState();
      
      // Add click handler
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleWishlist();
      });
    });
  }

  filterByCategory(category) {
    this.activeCategory = category;

    // Filter products
    this.productItems.forEach((item) => {
      const itemCategory = item.dataset.category;
      
      if (itemCategory === category) {
        item.classList.add('new-arrivals__product-item--visible');
        item.style.display = 'block';
      } else {
        item.classList.remove('new-arrivals__product-item--visible');
        item.style.display = 'none';
      }
    });

    // Update slider counter
    this.updateSliderCounter();

    // Reinitialize wishlist for visible products
    this.initWishlist();

    // Scroll to first visible product
    const firstVisible = this.section.querySelector('.new-arrivals__product-item--visible');
    if (firstVisible && this.slider) {
      const sliderContainer = this.slider.querySelector('.slider');
      if (sliderContainer) {
        const scrollPosition = firstVisible.offsetLeft - sliderContainer.offsetLeft;
        sliderContainer.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  }

  updateActiveTab(activeTab) {
    // Remove active class from all tabs
    this.categoryTabs.forEach((tab) => {
      tab.classList.remove('new-arrivals__category-tab--active');
      tab.setAttribute('aria-selected', 'false');
    });

    // Add active class to clicked tab
    activeTab.classList.add('new-arrivals__category-tab--active');
    activeTab.setAttribute('aria-selected', 'true');
  }

  updateSliderCounter() {
    const visibleItems = this.section.querySelectorAll('.new-arrivals__product-item--visible');
    const currentCounter = this.section.querySelector('.slider-counter--current');
    const totalCounter = this.section.querySelector('.slider-counter--total');

    if (totalCounter) {
      totalCounter.textContent = visibleItems.length;
    }

    // Reset current counter to 1
    if (currentCounter) {
      currentCounter.textContent = '1';
    }
  }
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  const newArrivalsSections = document.querySelectorAll('.section-new-arrivals');
  
  newArrivalsSections.forEach((section) => {
    const instance = new NewArrivalsSection(section);
    section._newArrivalsInstance = instance;
  });
});

// Listen for wishlist updates from other components
window.addEventListener('wishlist:updated', () => {
  const newArrivalsSections = document.querySelectorAll('.section-new-arrivals');
  newArrivalsSections.forEach((section) => {
    const sectionInstance = section._newArrivalsInstance;
    if (sectionInstance) {
      sectionInstance.initWishlist();
    }
  });
});

window.addEventListener('storage', (e) => {
  if (e.key === 'wishlist') {
    const newArrivalsSections = document.querySelectorAll('.section-new-arrivals');
    newArrivalsSections.forEach((section) => {
      const sectionInstance = section._newArrivalsInstance;
      if (sectionInstance) {
        sectionInstance.initWishlist();
      }
    });
  }
});

// Reinitialize when section is loaded in theme editor
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId && event.target.querySelector('.section-new-arrivals')) {
    const section = event.target.querySelector('.section-new-arrivals');
    if (section) {
      const instance = new NewArrivalsSection(section);
      section._newArrivalsInstance = instance;
    }
  }
});

// Also handle section:reorder and section:select events
document.addEventListener('shopify:section:reorder', (event) => {
  const newArrivalsSections = document.querySelectorAll('.section-new-arrivals');
  newArrivalsSections.forEach((section) => {
    const instance = new NewArrivalsSection(section);
    section._newArrivalsInstance = instance;
  });
});

document.addEventListener('shopify:section:select', (event) => {
  const section = event.target.querySelector('.section-new-arrivals');
  if (section) {
    const instance = new NewArrivalsSection(section);
    section._newArrivalsInstance = instance;
  }
});

